import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useReviewSkusV1TaxonomySkusGet } from "../api/generated/qcsControlPlane";
import type { Platform, TaxonomyReviewRow } from "../api/generated/model";
import { isPlatform, platformKeys, platformLabel } from "../api/platforms";
import { type AnyColumnDef, DataTable, createColumnHelper } from "../components/DataTable";
import { useBreadcrumbTail } from "../components/breadcrumbs";
import { Button, PageHeader, Panel, Row } from "@akaike/ui";

const PAGE = 50;

export function TaxonomyReview() {
  useBreadcrumbTail([{ label: "Taxonomy", to: "/taxonomy" }, { label: "Review" }]);

  const [platform, setPlatform] = useState<Platform>(platformKeys()[0]);
  const [source, setSource] = useState("");
  const [maxConf, setMaxConf] = useState("");
  const [page, setPage] = useState(0);

  const q = useReviewSkusV1TaxonomySkusGet(
    {
      platform,
      source: source || null,
      max_conf: maxConf ? Number(maxConf) : null,
      limit: PAGE,
      offset: page * PAGE,
    },
    { query: { refetchInterval: 20000 } },
  );
  const rows = q.data ?? [];

  const columns = useMemo<AnyColumnDef<TaxonomyReviewRow>[]>(() => {
    const col = createColumnHelper<TaxonomyReviewRow>();
    return [
      col.accessor((r) => r.name || r.product_id, {
        id: "product",
        header: "Product",
        cell: ({ row }) => (
          <Link to={`/skus/${platform}/${encodeURIComponent(row.original.product_id)}`}>
            {row.original.name || row.original.product_id}
          </Link>
        ),
        meta: { minWidth: 240 },
      }),
      col.accessor("brand", {
        header: "Brand",
        cell: ({ row }) => row.original.brand || "-",
        meta: { minWidth: 140 },
      }),
      col.accessor("leaf_path", {
        header: "Canonical node",
        cell: ({ row }) => (
          <span className="mono" style={{ fontSize: 11.5 }}>
            {row.original.leaf_path || "(unmapped)"}
          </span>
        ),
        meta: { minWidth: 220 },
      }),
      col.accessor("source", {
        header: "Source",
        cell: ({ row }) => (
          <span className={`taxo-src taxo-src--${row.original.source}`}>{row.original.source}</span>
        ),
        meta: { width: 110 },
      }),
      col.accessor("confidence", {
        header: "Conf.",
        cell: ({ row }) => row.original.confidence.toFixed(2),
        meta: { align: "right", width: 90, cellClassName: "num" },
      }),
    ];
  }, [platform]);

  return (
    <div className="view">
      <PageHeader
        className="mb-3.5"
        title="Review queue"
        description="Labelled SKUs, lowest-confidence first. Click a row to correct it on the SKU page."
      />

      <Panel className="mb-3">
        <Panel.Body>
          <Row gap="lg" align="end" wrap>
            <label className="col gap-1">
              <span className="sub">Platform</span>
              <select
                className="input"
                value={platform}
                onChange={(e) => {
                  if (!isPlatform(e.target.value)) return;
                  setPlatform(e.target.value);
                  setPage(0);
                }}
              >
                {platformKeys().map((p) => (
                  <option key={p} value={p}>
                    {platformLabel(p)}
                  </option>
                ))}
              </select>
            </label>
            <label className="col gap-1">
              <span className="sub">Source</span>
              <select
                className="input"
                value={source}
                onChange={(e) => {
                  setSource(e.target.value);
                  setPage(0);
                }}
              >
                <option value="">all</option>
                <option value="ai">ai</option>
                <option value="override">override</option>
              </select>
            </label>
            <label className="col gap-1">
              <span className="sub">Max confidence</span>
              <select
                className="input"
                value={maxConf}
                onChange={(e) => {
                  setMaxConf(e.target.value);
                  setPage(0);
                }}
              >
                <option value="">any</option>
                <option value="0.5">&lt;= 0.50</option>
                <option value="0.7">&lt;= 0.70</option>
                <option value="0.9">&lt;= 0.90</option>
              </select>
            </label>
            <div className="sub ml-auto">
              page {page + 1}
              {q.isLoading ? " - loading..." : ` - ${rows.length} rows`}
            </div>
          </Row>
        </Panel.Body>
      </Panel>

      <Panel>
        <Panel.Body tight>
          <DataTable
            tableKey="taxonomy-review"
            fillHeight
            columns={columns}
            data={rows}
            getRowId={(r) => r.product_id}
            globalPlaceholder="Search SKUs…"
            csvFilename="taxonomy-review"
            emptyMessage="No labelled SKUs match this filter. Run a Classify SKUs job, or loosen the filter."
            isLoading={q.isLoading}
          />
        </Panel.Body>
      </Panel>

      <Row gap="sm" justify="center" className="mt-3">
        <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>
          Prev
        </Button>
        <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={rows.length < PAGE}>
          Next
        </Button>
      </Row>
    </div>
  );
}
